import axios from 'axios'
import * as crypto from 'crypto'

import { Forbidden, NotFoundError } from '@pff-consumer/api-common/lib/errors'
import type {
  GetGrootAccessTokenParams,
  GetGrootAccessTokenResponse,
  GetGrootAuthorizeStateParams,
  GetGrootAuthorizeStateResponse,
  GetGrootMeParams,
  GetGrootMeResponse,
  GrootClientToken,
  GrootJWT,
  GrootTokenParams,
  UpdateGrootMeParams,
  UpdateGrootMeResponse,
} from './types'
import { GetGrootRefreshTokenParams, GetGrootRefreshTokenResponse } from './types/get-groot-refresh-token'
import {
  AppleParams,
  BaseGrootClientTokenResponse,
  BaseSubParams,
  CreateSubParams,
  GrootTokenUserResponse,
  GrootUserResponse,
  SignupParams,
  SyncSubParams,
} from '@pff-consumer/schema/groot/groot-client'

interface GrootClientParams {
  grootUrl: string
  grootClientId: string
  grootClientSecret: string
  consumerGrootKey?: string
  grootAccessKey?: string
}

export interface GrootAuthToken {
  token_type: string
  access_token: string
  expires_in: number
  refresh_token: string
}

export class GrootClient {
  private grootUrl: string

  private grootClientId: string

  private grootClientSecret: string

  private consumerGrootKey: string | undefined

  private grootAccessKey: string | undefined

  constructor(params: GrootClientParams) {
    const { grootUrl, grootClientId, grootClientSecret, consumerGrootKey, grootAccessKey } = params
    if (!grootUrl) {
      throw new Error('Error initializing GrootClient grootUrl is required')
    }
    if (!grootClientId) {
      throw new Error('Error initializing GrootClient grootClientId is required')
    }
    if (!grootClientSecret) {
      throw new Error('Error initializing GrootClient grootClientSecret is required')
    }

    // Unfortunately many places in our existing codebase pass the env var as the base domain without the protocol
    // but we need to have the protocol available for the axios calls to work properly so I decided to fix here
    // instead of passing multiple versions fo the same env var
    if (grootUrl.includes('https://') || grootUrl.includes('http://')) {
      this.grootUrl = grootUrl
    } else {
      this.grootUrl = `https://${grootUrl}`
    }
    this.grootClientId = grootClientId
    this.grootClientSecret = grootClientSecret
    this.consumerGrootKey = consumerGrootKey
    this.grootAccessKey = grootAccessKey
  }

  public getGrootAuthorizeState({ redirectUrl }: GetGrootAuthorizeStateParams): GetGrootAuthorizeStateResponse {
    const state = crypto.randomBytes(20).toString('hex')
    const encodedRedirectUrl = encodeURIComponent(redirectUrl)
    const url = `${this.grootUrl}/v2/oauth/authorize?client_id=${this.grootClientId}&redirect_uri=${encodedRedirectUrl}&response_type=code&state=${state}`
    return {
      state,
      url,
    }
  }

  public async getGrootMe({ accessToken }: GetGrootMeParams): Promise<GetGrootMeResponse> {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    const url = `${this.grootUrl}/api/v1/users/me`
    try {
      const { data } = await axios.get<GetGrootMeResponse>(url, config)
      return data
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (Number(e.response?.status) === 403) {
          throw new Forbidden(`403 response from /api/v1/users/me`, e)
        }
        if (Number(e.response?.status) === 404) {
          throw new NotFoundError(`404 response from /api/v1/users/me`, e)
        }
      }
      throw e
    }
  }

  public async getGrootMeWithJWT({ jwt }: { jwt: string }): Promise<GetGrootMeResponse> {
    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    }

    const url = `${this.grootUrl}/api/consumer/v2/user/me?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    try {
      const { data } = await axios.get<GetGrootMeResponse>(url, config)
      return data
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (Number(e.response?.status) === 403) {
          throw new Forbidden(`403 response from /api/consumer/v2/user/me`, e)
        }
        if (Number(e.response?.status) === 404) {
          throw new NotFoundError(`404 response from /api/consumer/v2/user/me`, e)
        }
      }
      throw e
    }
  }

  public async refreshJWT({ jwt }: { jwt: string }): Promise<GrootJWT> {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'x-pff-source-app': 'consumer-api',
        Authorization: `Bearer ${jwt}`,
      },
    }

    const url = `${this.grootUrl}/api/consumer/v2/token/refresh?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    const { data } = await axios.get<GrootJWT>(url, config)
    return data
  }

  public async updateGrootMe({ accessToken, updateParams }: UpdateGrootMeParams): Promise<UpdateGrootMeResponse> {
    const url = `${this.grootUrl}/api/v1/users/me?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    const requestBody = {
      user: {
        current_password: updateParams.currentPassword,
        password: updateParams.password,
        password_confirmation: updateParams.password,
        first_name: updateParams.firstName,
        last_name: updateParams.lastName,
      },
    }
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    const { data } = await axios.put<UpdateGrootMeResponse>(url, requestBody, config)
    return data
  }

  public async getGrootAccessToken({
    code,
    redirectUrl,
  }: GetGrootAccessTokenParams): Promise<GetGrootAccessTokenResponse> {
    const encodedRedirectUrl = encodeURIComponent(redirectUrl)
    const url = `${this.grootUrl}/v2/oauth/token?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}&grant_type=authorization_code&redirect_uri=${encodedRedirectUrl}&code=${code}`
    const { data } = await axios.post<GetGrootAccessTokenResponse>(url)
    return data
  }

  public async getGrootJWT({ email, password }: { email: string; password: string }): Promise<GrootJWT> {
    const requestBody = {
      email,
      password,
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'x-pff-source-app': 'consumer-api',
      },
    }

    const url = `${this.grootUrl}/api/consumer/v2/token?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    const { data } = await axios.post<GrootJWT>(url, requestBody, config)
    return data
  }

  public async getGrootJWTByToken({
    accessToken,
    refreshToken,
  }: {
    accessToken: string
    refreshToken: string
  }): Promise<GrootJWT> {
    const requestBody = {
      access_token: accessToken,
      refresh_token: refreshToken,
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'x-pff-source-app': 'consumer-api',
      },
    }

    const url = `${this.grootUrl}/api/consumer/v2/token?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    const { data } = await axios.post<GrootJWT>(url, requestBody, config)
    return data
  }

  public async getGrootRefreshToken({ accessToken, refreshToken }: GetGrootRefreshTokenParams) {
    const url = `${this.grootUrl}/v2/oauth/token?grant_type=refresh_token&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    const requestBody = {
      refresh_token: refreshToken,
    }
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Key': accessToken,
        'x-pff-source-app': 'consumer-api',
      },
    }
    const { data } = await axios.post<GetGrootRefreshTokenResponse>(url, requestBody, config)
    return data
  }

  /*
    Will create a user with an email and password
  */
  public async createUserWithEmail({
    email,
    password,
  }: GrootTokenParams): Promise<{ success: boolean; error: string | null }> {
    const tok = await this.getClientToken()

    const url = `${this.grootUrl}/api/v1/users/me?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    const requestBody = {
      email,
      password,
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${tok.access_token}`,
        'x-pff-source-app': 'consumer-groot-client',
      },
    }

    try {
      const { data } = await axios.post<{ success: boolean }>(url, requestBody, config)
      return {
        ...data,
        error: null,
      }
    } catch (e: any) {
      return {
        success: false,
        error: e.response.data.error.email[0],
      }
    }
  }

  /*
    This is needed to make calls to Groot. It uses client credentials based on an API user
    generated in groot. In order for this call to work, there needs to be an API key  created and
    passed to the groot client.

  */
  public async getClientToken(): Promise<GrootClientToken> {
    if (!this.consumerGrootKey) {
      throw new Error('Consumer Groot key is needed to make this request')
    }

    const url = `${this.grootUrl}/v2/oauth/token?grant_type=client_credentials&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${this.consumerGrootKey}`,
        'x-pff-source-app': 'consumer-groot-client',
      },
    }

    const { data } = await axios.post<GrootClientToken>(url, {}, config)
    return data
  }

  /*
    This will mint a user token based on credentials
  */
  public async getUserToken({ email, password }: GrootTokenParams): Promise<GetGrootAccessTokenResponse> {
    const tok = await this.getClientToken()

    const requestBody = {
      username: email,
      password,
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${tok.access_token}`,
        'x-pff-source-app': 'consumer-groot-client',
      },
    }

    const url = `${this.grootUrl}/v2/oauth/token?grant_type=password&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    const { data } = await axios.post<GetGrootAccessTokenResponse>(url, requestBody, config)
    return data
  }

  public async logoutGroot(): Promise<void> {
    const tok = await this.getClientToken()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${tok.access_token}`,
        'x-pff-source-app': 'consumer-groot-client',
      },
    }

    const url = `${this.grootUrl}/sso/logout?grant_type=password&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    await axios.get<GetGrootAccessTokenResponse>(url, config)
  }

  private errorHandler(error: unknown): never {
    console.error(error)
    if (axios.isAxiosError(error)) {
      if (error.response?.data) {
        throw new Error(error.response?.data?.error_description)
      }
      throw new Error('Auth server returned an unknown error response. Please try again later.')
    }
    if (error instanceof Error) {
      throw error
    }
    throw new Error('An error occurred while trying to call the auth token server, or when parsing the response.')
  }

  public async authToken(username: string, password: string): Promise<GrootAuthToken> {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Key': this.grootAccessKey,
        'x-pff-source-app': 'consumer-api',
      },
    }
    const url = `${this.grootUrl}/api/consumer/user/token?grant_type=password&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    try {
      const { data: result } = await axios.post<GrootAuthToken>(url, { username, password }, config)

      if (
        typeof result.access_token === 'string' &&
        typeof result.refresh_token === 'string' &&
        typeof result.expires_in === 'number' &&
        result.token_type === 'Bearer'
      ) {
        return result
      }
      throw new Error('Unknown response returned from auth server. Please try again later.')
    } catch (error) {
      this.errorHandler(error)
    }
  }

  public async refreshToken(refreshTok: string): Promise<GrootAuthToken> {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Key': this.grootAccessKey,
        'x-pff-source-app': 'consumer-api',
      },
    }
    const url = `${this.grootUrl}/api/consumer/user/token?grant_type=refresh_token&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`

    try {
      const { data: result } = await axios.post<GrootAuthToken>(url, { refresh_token: refreshTok }, config)

      if (
        typeof result.access_token === 'string' &&
        typeof result.refresh_token === 'string' &&
        typeof result.expires_in === 'number' &&
        result.token_type === 'Bearer'
      ) {
        return result
      }
      throw new Error('Unknown response returned from auth server. Please try again later.')
    } catch (error) {
      this.errorHandler(error)
    }
  }

  public async codeToken(code: string): Promise<GrootAuthToken> {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Key': this.grootAccessKey,
        'x-pff-source-app': 'consumer-api',
      },
    }
    const url = `${this.grootUrl}/api/consumer/user/token?grant_type=authorization_code&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    try {
      const { data: result } = await axios.post<GrootAuthToken>(url, { code, redirect_uri: 'https://pff.com' }, config)

      if (
        typeof result.access_token === 'string' &&
        typeof result.refresh_token === 'string' &&
        typeof result.expires_in === 'number' &&
        result.token_type === 'Bearer'
      ) {
        return result
      }
      throw new Error('Unknown response returned from auth server. Please try again later.')
    } catch (error) {
      this.errorHandler(error)
    }
  }

  public async clientToken(): Promise<BaseGrootClientTokenResponse> {
    const url = `${this.grootUrl}/api/consumer/user/token?grant_type=client_credentials&client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Key': this.grootAccessKey,
        'x-pff-source-app': 'consumer-api',
        authorization: `Bearer ${this.consumerGrootKey}`,
      },
    }
    try {
      const { data: result } = await axios.post<GrootAuthToken>(url, undefined, config)

      if (
        typeof result.access_token === 'string' &&
        typeof result.expires_in === 'number' &&
        result.token_type === 'Bearer'
      ) {
        return result
      }
      throw new Error('Unknown response returned from auth server. Please try again later.')
    } catch (error: any) {
      this.errorHandler(error)
    }
  }

  public async getUser(params: GrootAuthToken): Promise<GrootTokenUserResponse> {
    const url = `${this.grootUrl}/api/v1/users/me?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${params.access_token}`,
        'x-pff-source-app': 'consumer-api',
      },
    }
    try {
      const { data } = await axios.get<GrootUserResponse>(url, config)
      return {
        token: params,
        user: data,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error === 'Forbidden') {
        const refresh = await this.refreshToken(params.refresh_token)
        const { user } = await this.getUser(refresh)
        return {
          token: refresh,
          user,
        }
      }
      console.error(error)
      throw new Error('Unable to get user details with auth token.')
    }
  }

  public async createUser(params: SignupParams): Promise<GrootAuthToken> {
    const url = `${this.grootUrl}/api/consumer/user?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Key': this.grootAccessKey,
        'x-pff-source-app': 'consumer-api',
      },
    }
    try {
      if (params.email && params.password) {
        const { data: result } = await axios.post<GrootAuthToken>(
          url,
          { email: params.email, password: params.password },
          config
        )

        if (result) {
          return await this.authToken(params.email, params.password)
        } else {
          throw new Error('Signup server returned an unknown response. Please try again later.')
        }
      } else if (params.provider && params.providerId) {
        throw new Error('Unable to signup with provider.')
      } else {
        throw new Error('Missing required signup params.')
      }
    } catch (error) {
      console.error(error)
      if (axios.isAxiosError(error) && error.response?.data) {
        throw new Error(error.response?.data?.message)
      } else {
        throw new Error('An error occurred while trying to call the signup server, or when parsing the response.')
      }
    }
  }

  public async createSubscription(params: CreateSubParams): Promise<any> {
    const url = `${this.grootUrl}/api/v1/consumers/subscriptions/create`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${params.token}`,
        'x-pff-source-app': 'consumer-api',
      },
    }
    try {
      const { data } = await axios.post(
        url,
        {
          user_uid: params.user_uid,
          role: params.role,
          start_date: params.start_date,
          end_date: params.end_date,
        },
        config
      )
      return data
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error
      } else {
        throw new Error('An error occurred while updating the user subscription')
      }
    }
  }

  public async renewSubscription(params: BaseSubParams, accessToken: string): Promise<void> {
    const url = `${this.grootUrl}/api/v1/consumers/subscriptions/renew`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
        'x-pff-source-app': 'consumer-api',
      },
    }
    try {
      const { data } = await axios.post(
        url,
        {
          user_uid: params.user_uid,
          role: params.role,
          end_date: params.end_date,
        },
        config
      )
      return data
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error
      } else {
        throw new Error('An error occurred while updating the user subscription')
      }
    }
  }

  public async cancelSubscription(params: BaseSubParams, accessToken: string): Promise<void> {
    const url = `${this.grootUrl}/api/v1/consumers/subscriptions/cancel`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
        'x-pff-source-app': 'consumer-api',
      },
    }
    try {
      await axios.post<GrootAuthToken>(
        url,
        {
          user_uid: params.user_uid,
          role: params.role,
          end_date: params.end_date,
        },
        config
      )
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error
      } else {
        throw new Error('An error occurred while canceling the user subscription')
      }
    }
  }

  public async grootAppleLogin(params: AppleParams): Promise<any> {
    const { id_token, first_name, last_name, nonce, client_id } = params
    const url = `${this.grootUrl}/social-auth/apple`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Key': this.grootAccessKey,
        'x-pff-source-app': 'consumer-api',
      },
    }
    try {
      const { data: result } = await axios.post<GrootAuthToken>(
        url,
        {
          id_token,
          first_name,
          last_name,
          nonce,
          client_id,
          redirect_uri: 'https://pff.com',
        },
        config
      )
      return result
    } catch (error) {
      console.error(error)
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error_description) {
          throw new Error(error.response.data.error_description)
        }
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error)
        }
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message)
        }
        throw error
      } else {
        throw new Error('An error occurred while trying to call the signup server, or when parsing the response.')
      }
    }
  }

  public async findUserByEmail(email: string, accessToken: string): Promise<GrootUserResponse> {
    return this.findUser({ searchTerm: email, accessToken, type: 'email' })
  }

  public async findUserByUid(grootUid: string, accessToken: string): Promise<GrootUserResponse> {
    return this.findUser({ searchTerm: grootUid, accessToken, type: 'uid' })
  }

  private async findUser({
    searchTerm,
    accessToken,
    type,
  }: {
    searchTerm: string
    accessToken: string
    type: 'email' | 'uid'
  }): Promise<GrootUserResponse> {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
        'x-pff-source-app': 'consumer-api',
      },
    }
    try {
      const { data } = await axios.post<GrootUserResponse>(
        `${this.grootUrl}/api/v1/admin/users/find`,
        {
          term: searchTerm,
          type,
        },
        config
      )
      return data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return error.response.data
      }
      throw new Error('An error occurred.')
    }
  }

  public syncSubscriptions = async (userUid: string, params: SyncSubParams[], accessToken: string): Promise<void> => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${accessToken}`,
          'x-pff-source-app': 'consumer-api',
        },
      }

      await axios.post(
        `${this.grootUrl}/api/v1/admin/users/${userUid}/sync`,
        {
          subscriptions: params,
        },
        config
      )
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  public deleteUser = async (userUid: string, accessToken: string): Promise<void> => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${accessToken}`,
          'x-pff-source-app': 'consumer-api',
        },
      }

      await axios.post(`${this.grootUrl}/api/v1/admin/users/${userUid}/delete`, {}, config)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  public async updateUserProfile({
    firstName,
    lastName,
    jwtToken,
  }: {
    firstName: string
    lastName?: string
    jwtToken: string
  }): Promise<GetGrootMeResponse> {
    const requestBody = {
      first_name: firstName,
      last_name: lastName,
    }
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'x-pff-source-app': 'consumer-api',
        Authorization: `Bearer ${jwtToken}`,
      },
    }

    try {
      const url = `${this.grootUrl}/api/consumer/v2/user/me/update?client_id=${this.grootClientId}&client_secret=${this.grootClientSecret}`
      const { data } = await axios.put<GetGrootMeResponse>(url, requestBody, config)
      return data
    } catch (error) {
      console.error(error)
      throw new Error('Unable to update user details with auth token.')
    }
  }
}
